// pages/ServerAccess/ServerAccess.jsx
//
// Super-admin "Server Access" vault: how to reach the brand servers (Prigmar,
// BidLight, free-builder, …). Metadata is shown inline; credentials are
// encrypted at rest and masked here until a super-admin clicks Reveal (which
// hits the audited /reveal endpoint). superAdmin only.

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { get, post, globalPut, remove } from "../../api";

const BASE = "/api/admin/server-access";
const AUTH_TYPES = ["key", "password", "none"];
const META_FIELDS = [
  ["serverKey", "Key (prigmar/bidlight/…)"], ["label", "Label"], ["host", "Host"],
  ["port", "Port"], ["sshUser", "SSH user"], ["authType", "Auth type"],
  ["keyPath", "Key path (on this box)"], ["sshOptions", "SSH options"],
  ["appPath", "App path"], ["services", "Services"], ["dbInfo", "DB info"], ["notes", "Notes / gotchas"],
];
const BLANK = { serverKey: "", label: "", host: "", port: 22, sshUser: "root", authType: "key", keyPath: "", sshOptions: "", appPath: "", services: "", dbInfo: "", notes: "", secrets: [] };

function copy(txt) { try { navigator.clipboard.writeText(txt); } catch (e) { /* noop */ } }

export default function ServerAccess() {
  const userInfo = useSelector((s) => s.user.userInfo);
  const isSuperAdmin = !!(userInfo && userInfo.superAdmin);

  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState({}); // id -> [{label,value,hint}]
  const [editing, setEditing] = useState(null); // doc being edited/created (with secrets having plaintext value)

  const fetchList = useCallback(async () => {
    try { const r = await get(BASE); setRows((r && r.servers) || []); setErr(null); }
    catch (e) { setErr((e && e.message) || "Failed to load"); }
  }, []);

  useEffect(() => { if (isSuperAdmin) fetchList(); }, [fetchList, isSuperAdmin]);

  if (!isSuperAdmin) return <Redirect to="/dashboard" />;

  const reveal = async (id) => {
    setErr(null);
    try { const r = await post(`${BASE}/${id}/reveal`, {}, false); setRevealed((m) => ({ ...m, [id]: r.secrets || [] })); }
    catch (e) { setErr((e && e.message) || "Reveal failed"); }
  };
  const hide = (id) => setRevealed((m) => { const n = { ...m }; delete n[id]; return n; });

  const startCreate = () => setEditing({ ...BLANK, secrets: [] });
  const startEdit = (r) => setEditing({ ...r, secrets: (r.secrets || []).map((s) => ({ label: s.label, hint: s.hint, value: "" })) });

  const save = async () => {
    if (!editing.serverKey || !editing.label) return setErr("Key and Label are required");
    setBusy(true); setErr(null);
    try {
      const body = { ...editing, secrets: (editing.secrets || []).filter((s) => s.label) };
      if (editing._id) await globalPut(`${BASE}/${editing._id}`, body, false);
      else await post(BASE, body, false);
      setEditing(null); setMsg("Saved."); await fetchList();
    } catch (e) { setErr((e && e.message) || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this server record?")) return;
    setBusy(true);
    try { await remove(`${BASE}/${id}`, {}); await fetchList(); }
    catch (e) { setErr((e && e.message) || "Delete failed"); }
    finally { setBusy(false); }
  };

  const setE = (k, v) => setEditing((d) => ({ ...d, [k]: v }));
  const setSecret = (i, k, v) => setEditing((d) => { const s = [...d.secrets]; s[i] = { ...s[i], [k]: v }; return { ...d, secrets: s }; });
  const addSecret = () => setEditing((d) => ({ ...d, secrets: [...(d.secrets || []), { label: "", value: "", hint: "" }] }));
  const rmSecret = (i) => setEditing((d) => ({ ...d, secrets: d.secrets.filter((_, j) => j !== i) }));

  return (
    <div className="container py-4">
      <style>{`
        .sa-card { border:1px solid #e5e7eb; border-radius:10px; padding:16px; margin-bottom:14px; background:#fff; }
        .sa-key { font-size:12px; color:#fff; background:#111827; border-radius:6px; padding:2px 8px; margin-right:8px; }
        .sa-btn { border:none; border-radius:8px; padding:6px 12px; font-weight:600; cursor:pointer; margin-right:8px; }
        .sa-btn.primary { background:#2563eb; color:#fff; } .sa-btn.ghost { background:#f3f4f6; color:#374151; }
        .sa-btn.danger { background:#fee2e2; color:#b91c1c; } .sa-btn.green { background:#059669; color:#fff; }
        .sa-btn:disabled { opacity:.5; cursor:not-allowed; }
        .sa-field { display:block; width:100%; margin:4px 0 10px; padding:7px 10px; border:1px solid #d1d5db; border-radius:8px; font-family:inherit; }
        .sa-label { font-size:12px; font-weight:600; color:#374151; }
        .sa-meta { font-size:13px; color:#374151; margin:2px 0; } .sa-meta b { color:#111827; }
        .sa-secret { display:flex; align-items:center; gap:8px; padding:6px 10px; background:#f9fafb; border-radius:8px; margin:5px 0; font-size:13px; flex-wrap:wrap; }
        .sa-val { font-family:ui-monospace,Menlo,monospace; background:#111827; color:#a7f3d0; padding:2px 8px; border-radius:6px; word-break:break-all; }
        .sa-row { display:flex; gap:10px; flex-wrap:wrap; } .sa-row > div { flex:1; min-width:150px; }
        .sa-warn { font-size:12px; color:#92400e; background:#fffbeb; border:1px solid #fde68a; border-radius:8px; padding:8px 10px; margin-bottom:14px; }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Server Access</h3>
        {!editing && <button className="sa-btn primary" onClick={startCreate}>+ Add server</button>}
      </div>
      <div className="sa-warn">Credentials are encrypted at rest (SECRET_CRYPTO_KEY). They are masked until you click <b>Reveal</b>; treat revealed values as sensitive. Super-admin only.</div>

      {err && <div className="alert alert-warning">{err}</div>}
      {msg && <div className="alert alert-success">{msg}</div>}

      {editing && (
        <div className="sa-card">
          <h5>{editing._id ? "Edit" : "Add"} server</h5>
          <div className="sa-row">
            {META_FIELDS.map(([k, lbl]) => (
              <div key={k} style={{ minWidth: k === "notes" || k === "dbInfo" ? "100%" : 150 }}>
                <span className="sa-label">{lbl}</span>
                {k === "authType" ? (
                  <select className="sa-field" value={editing[k]} onChange={(e) => setE(k, e.target.value)}>
                    {AUTH_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                ) : k === "notes" || k === "dbInfo" ? (
                  <textarea className="sa-field" rows={2} value={editing[k] || ""} onChange={(e) => setE(k, e.target.value)} />
                ) : (
                  <input className="sa-field" value={editing[k] ?? ""} onChange={(e) => setE(k, k === "port" ? e.target.value : e.target.value)} />
                )}
              </div>
            ))}
          </div>

          <span className="sa-label">Secrets (leave value blank to keep the existing one)</span>
          {(editing.secrets || []).map((s, i) => (
            <div className="sa-row" key={i} style={{ alignItems: "flex-end" }}>
              <div><input className="sa-field" placeholder="label" value={s.label} onChange={(e) => setSecret(i, "label", e.target.value)} /></div>
              <div style={{ flex: 2 }}><input className="sa-field" placeholder="value (write-only)" value={s.value} onChange={(e) => setSecret(i, "value", e.target.value)} /></div>
              <div><input className="sa-field" placeholder="hint" value={s.hint} onChange={(e) => setSecret(i, "hint", e.target.value)} /></div>
              <button className="sa-btn danger" onClick={() => rmSecret(i)}>×</button>
            </div>
          ))}
          <button className="sa-btn ghost" onClick={addSecret}>+ secret</button>
          <div style={{ marginTop: 12 }}>
            <button className="sa-btn green" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
            <button className="sa-btn ghost" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {rows.map((r) => (
        <div className="sa-card" key={r._id}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><span className="sa-key">{r.serverKey}</span><strong>{r.label}</strong></div>
            <div>
              <button className="sa-btn ghost" onClick={() => startEdit(r)}>Edit</button>
              <button className="sa-btn danger" onClick={() => del(r._id)}>Delete</button>
            </div>
          </div>
          {r.host && <div className="sa-meta"><b>SSH:</b> {r.sshUser}@{r.host}{r.port && r.port !== 22 ? `:${r.port}` : ""} ({r.authType}{r.sshOptions ? ` ${r.sshOptions}` : ""}){r.keyPath ? ` · key ${r.keyPath}` : ""}</div>}
          {r.appPath && <div className="sa-meta"><b>App:</b> {r.appPath}</div>}
          {r.services && <div className="sa-meta"><b>Services:</b> {r.services}</div>}
          {r.dbInfo && <div className="sa-meta"><b>DB:</b> {r.dbInfo}</div>}
          {r.notes && <div className="sa-meta"><b>Notes:</b> {r.notes}</div>}

          {(r.secrets || []).length > 0 && (
            <div style={{ marginTop: 8 }}>
              {(revealed[r._id] || r.secrets).map((s, i) => {
                const shown = revealed[r._id];
                return (
                  <div className="sa-secret" key={i}>
                    <b>{s.label}</b>
                    {shown ? <span className="sa-val">{s.value || "(empty)"}</span> : <span style={{ color: "#9ca3af" }}>•••••••• {s.hasValue ? "" : "(unset)"}</span>}
                    {s.hint && <span style={{ color: "#9ca3af", fontSize: 12 }}>{s.hint}</span>}
                    {shown && s.value && <button className="sa-btn ghost" style={{ padding: "2px 8px" }} onClick={() => copy(s.value)}>copy</button>}
                  </div>
                );
              })}
              {revealed[r._id]
                ? <button className="sa-btn ghost" onClick={() => hide(r._id)}>Hide</button>
                : <button className="sa-btn primary" onClick={() => reveal(r._id)}>Reveal secrets</button>}
              {r.lastRevealedAt && <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>last revealed {new Date(r.lastRevealedAt).toLocaleString()}{r.lastRevealedBy ? ` by ${r.lastRevealedBy}` : ""}</span>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

ServerAccess._test = { BLANK, AUTH_TYPES };
